import { useTranslation } from 'react-i18next';
import {
  ContentLibraryCategoryDto,
  ContentLibraryCategoryItemDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  LoadingIndicator,
  RadioGroup,
  SingleSelect,
} from '@asset-metrix/portal-ui';

import styles from './addComponentToDashboardModal.module.scss';

const RADIO_CARD_ITEM_LIMIT = 4;

function categoryItemSelectLabel(item: ContentLibraryCategoryItemDto): string {
  const description = item.description?.trim();
  if (!description) {
    return item.label;
  }
  return `${item.label} (${description})`;
}

function CategoryField({
  category,
  onChange,
  value,
}: {
  category: ContentLibraryCategoryDto;
  onChange: (tagName: string | null) => void;
  value: string | null;
}) {
  if (category.items.length <= RADIO_CARD_ITEM_LIMIT) {
    return (
      <RadioGroup
        label={category.name}
        name={`content-library-category-${category.id}`}
        onChange={onChange}
        options={category.items.map((item) => ({
          description: item.description ?? undefined,
          label: item.label,
          value: item.tagName,
        }))}
        value={value ?? ''}
        variant="cards"
        direction="row"
      />
    );
  }

  return (
    <SingleSelect
      clearable={false}
      data={category.items.map((item) => ({
        label: categoryItemSelectLabel(item),
        value: item.tagName,
      }))}
      label={category.name}
      onChange={onChange}
      value={value}
    />
  );
}

export type SelectDataSourceStepProps = {
  categories: ContentLibraryCategoryDto[];
  categorySelections: Record<string, string>;
  isError: boolean;
  isPending: boolean;
  isSuccess: boolean;
  onCategoryChange: (categoryId: string, tagName: string | null) => void;
};

export function SelectDataSourceStep({
  categories,
  categorySelections,
  isError,
  isPending,
  isSuccess,
  onCategoryChange,
}: SelectDataSourceStepProps) {
  const { t } = useTranslation();

  if (isPending) {
    return (
      <LoadingIndicator
        className={styles['loading']}
        data-testid="add-components-categories-loading"
        size="2em"
      />
    );
  }

  if (isError) {
    return (
      <p className={styles['message']}>
        {t('app.page.editAction.addComponentModal.loadError')}
      </p>
    );
  }

  if (isSuccess && categories.length === 0) {
    return (
      <p className={styles['message']}>
        {t('app.page.editAction.addComponentModal.emptyCategories')}
      </p>
    );
  }

  if (!isSuccess) {
    return null;
  }

  return (
    <>
      {categories.map((category) => (
        <div className={styles['category']} key={category.id}>
          <CategoryField
            category={category}
            onChange={(tagName) => onCategoryChange(category.id, tagName)}
            value={categorySelections[category.id] ?? null}
          />
        </div>
      ))}
    </>
  );
}
